import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
  effect,
  inject,
  input,
} from '@angular/core';

/**
 * Apply to a wrapper element that contains a single scrollable child.
 * Injects edge fade overlays that appear when there is hidden content in
 * that direction — top/bottom for vertical scroll, left/right for
 * horizontal. Uses a ResizeObserver on the scroller's inner content so
 * fades update when async data loads.
 *
 * The child is auto-detected: an element whose class contains `overflow`
 * (`overflow-y-auto`, `overflow-x-auto`, `overflow-auto`, ...).
 *
 * Usage:
 *   <div cosmosScrollFade class="relative">
 *     <div class="max-h-96 overflow-y-auto">...</div>
 *   </div>
 *
 *   <!-- default vertical fades, subtler + narrower horizontal ones -->
 *   <div cosmosScrollFade [fadeSizeX]="'0.75rem'" [fadeOpacityX]="0.1" class="relative">
 *     <div class="overflow-auto">...wide + tall content...</div>
 *   </div>
 *
 *   <!-- horizontal only, custom color -->
 *   <div cosmosScrollFade [fadeAxis]="'x'" [fadeColor]="'var(--surface-500)'" class="relative">
 *     <div class="overflow-x-auto">...wide content...</div>
 *   </div>
 *
 * Inputs: fadeAxis ('both'|'x'|'y'), fadeSizeX, fadeSizeY (CSS lengths),
 * fadeOpacity (0–1, both axes), fadeOpacityX / fadeOpacityY (per-axis
 * overrides), fadeColor (any CSS color).
 */
@Directive({ selector: '[cosmosScrollFade]', standalone: true })
export class ScrollFadeDirective implements AfterViewInit, OnDestroy {
  private el = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);

  /** Which axes get fades. 'both' (default), 'x' (left/right only), 'y' (top/bottom only). */
  readonly fadeAxis = input<'both' | 'x' | 'y'>('both');
  /** Height of the top/bottom fades (any CSS length). Default '1.5rem'. */
  readonly fadeSizeY = input<string>('1.5rem');
  /** Width of the left/right fades (any CSS length). Default '1rem' — a touch
   *  narrower than the vertical fades, since side-scroll is less common. */
  readonly fadeSizeX = input<string>('1rem');
  /** Peak opacity (0–1) of every fade. Overridden per-axis by fadeOpacityX /
   *  fadeOpacityY when those are set. Default 0.2. */
  readonly fadeOpacity = input<number>(0.2);
  /** Peak opacity (0–1) of the left/right fades. Falls back to fadeOpacity. */
  readonly fadeOpacityX = input<number | undefined>(undefined);
  /** Peak opacity (0–1) of the top/bottom fades. Falls back to fadeOpacity. */
  readonly fadeOpacityY = input<number | undefined>(undefined);
  /** Gradient color. Any CSS color; defaults to the PrimeNG primary color. */
  readonly fadeColor = input<string>('var(--p-primary-color)');

  private scroller: HTMLElement | null = null;
  private topFade!: HTMLElement;
  private bottomFade!: HTMLElement;
  private leftFade!: HTMLElement;
  private rightFade!: HTMLElement;
  private removeScrollListener!: () => void;
  private resizeObserver!: ResizeObserver;
  private ready = false;

  constructor() {
    // Restyle the overlays whenever any config input changes (post-init).
    effect(() => {
      // touch the inputs so the effect tracks them
      this.fadeAxis();
      this.fadeSizeX();
      this.fadeSizeY();
      this.fadeOpacity();
      this.fadeOpacityX();
      this.fadeOpacityY();
      this.fadeColor();
      if (this.ready) this.styleFades();
    });
  }

  ngAfterViewInit(): void {
    const host: HTMLElement = this.el.nativeElement;

    this.scroller = host.querySelector<HTMLElement>('[class*="overflow"]') ?? null;
    if (!this.scroller) return;

    this.topFade = this.renderer.createElement('div');
    this.bottomFade = this.renderer.createElement('div');
    this.leftFade = this.renderer.createElement('div');
    this.rightFade = this.renderer.createElement('div');

    for (const fade of [this.topFade, this.bottomFade, this.leftFade, this.rightFade]) {
      this.renderer.appendChild(host, fade);
    }

    this.ready = true;
    this.styleFades();

    this.removeScrollListener = this.renderer.listen(this.scroller, 'scroll', () => this.update());

    // Watch the scroller's inner content for size changes (e.g. async data loading).
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.scroller);
    if (this.scroller.firstElementChild) {
      this.resizeObserver.observe(this.scroller.firstElementChild);
    }

    this.update();
  }

  ngOnDestroy(): void {
    this.removeScrollListener?.();
    this.resizeObserver?.disconnect();
  }

  /** (Re)applies the static styles for all four overlays from the current inputs. */
  private styleFades(): void {
    const base = `
      pointer-events: none;
      position: absolute;
      opacity: 0;
      transition: opacity 200ms ease;
      z-index: 1;
    `;
    const axis = this.fadeAxis();
    const sy = this.fadeSizeY();
    const sx = this.fadeSizeX();
    const color = this.fadeColor();
    const mix = (o: number) =>
      `color-mix(in srgb, ${color} ${Math.round(o * 100)}%, transparent)`;
    const oX = mix(this.fadeOpacityX() ?? this.fadeOpacity());
    const oY = mix(this.fadeOpacityY() ?? this.fadeOpacity());
    const vHidden = axis === 'x' ? 'display: none;' : '';
    const hHidden = axis === 'y' ? 'display: none;' : '';

    this.renderer.setAttribute(this.topFade, 'style',
      `${base} ${vHidden} top: 0; left: 0; right: 0; height: ${sy};
       background: linear-gradient(to bottom, ${oY} 0%, transparent 100%);`);
    this.renderer.setAttribute(this.bottomFade, 'style',
      `${base} ${vHidden} bottom: 0; left: 0; right: 0; height: ${sy};
       background: linear-gradient(to top, ${oY} 0%, transparent 100%);`);
    this.renderer.setAttribute(this.leftFade, 'style',
      `${base} ${hHidden} top: 0; bottom: 0; left: 0; width: ${sx};
       background: linear-gradient(to right, ${oX} 0%, transparent 100%);`);
    this.renderer.setAttribute(this.rightFade, 'style',
      `${base} ${hHidden} top: 0; bottom: 0; right: 0; width: ${sx};
       background: linear-gradient(to left, ${oX} 0%, transparent 100%);`);

    this.update();
  }

  private update(): void {
    if (!this.scroller) return;
    const {
      scrollTop, scrollHeight, clientHeight,
      scrollLeft, scrollWidth, clientWidth,
    } = this.scroller;

    const axis = this.fadeAxis();
    const hasV = axis !== 'x' && scrollHeight > clientHeight + 1;
    const hasH = axis !== 'y' && scrollWidth > clientWidth + 1;

    this.setOpacity(this.topFade, hasV && scrollTop > 2 ? 1 : 0);
    this.setOpacity(this.bottomFade, hasV && scrollTop + clientHeight < scrollHeight - 2 ? 1 : 0);
    this.setOpacity(this.leftFade, hasH && scrollLeft > 2 ? 1 : 0);
    this.setOpacity(this.rightFade, hasH && scrollLeft + clientWidth < scrollWidth - 2 ? 1 : 0);
  }

  private setOpacity(el: HTMLElement, value: number): void {
    if (el) this.renderer.setStyle(el, 'opacity', String(value));
  }
}
